import { AuthQueryProvider } from "@daveyplate/better-auth-tanstack";
import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	Scripts,
} from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { lazy, Suspense, useEffect } from "react";
import { AddAgentDialogProvider } from "@/components/AddAgentDialog";
import { AdvertiseDialogProvider } from "@/components/AdvertiseDialog";
import { ClaimWebsiteDialogProvider } from "@/components/ClaimWebsiteDialog";
import { JsonLd } from "@/components/JsonLd";
import { LazyMotionProvider } from "@/components/LazyMotionProvider";
import { ListAgentDialogProvider } from "@/components/ListAgentDialog";
import { NotFoundView } from "@/components/NotFoundView";
import { PostHogAnalyticsProvider } from "@/components/PostHogAnalytics";
import { SellStartupDialogProvider } from "@/components/SellStartupDialog";
import { SubscriptionProvider } from "@/hooks/use-subscription";
import { useThemeShortcut } from "@/hooks/use-theme-shortcut";
import { getAdsData } from "@/lib/agentmrr-ads";
import { currencyAtom, detectDefaultCurrency } from "@/lib/currency";
import { organizationSchema, websiteSchema } from "@/lib/schema";
import { ThemeProvider } from "@/utils/themeProvider";

import appCss from "../styles.css?url";

// Lazy-mounted so sonner loads in a post-paint chunk, not the entry bundle.
const AppToaster = lazy(() =>
	import("@/components/AppToaster").then((m) => ({ default: m.AppToaster })),
);

if (import.meta.env.DEV && typeof window !== "undefined") {
	void import("react-grab");
}

interface MyRouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	loader: () => getAdsData(),
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1, viewport-fit=cover",
			},
			{
				title: "AgentMRR - Revenue Tracking for AI Agents",
			},
		],
		links: [
			{
				rel: "stylesheet",
				href: appCss,
			},
			{
				rel: "icon",
				type: "image/svg+xml",
				href: "/app-icon.svg",
			},
			// Raster fallbacks for everything that cannot render the SVG: browsers
			// without SVG icon support, and the favicon crawlers that directories
			// use to pull a site logo. Both point at the same black app-icon tile
			// so those surfaces never fall back to a stale third-party favicon.
			{
				rel: "icon",
				type: "image/png",
				sizes: "512x512",
				href: "/app-icon-512.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "192x192",
				href: "/app-icon-192.png",
			},
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/apple-touch-icon.png",
			},
			{ rel: "preconnect", href: "https://www.googletagmanager.com" },
			{ rel: "preconnect", href: "https://www.google-analytics.com" },
			{ rel: "dns-prefetch", href: "https://api.stripe.com" },
		],
	}),

	shellComponent: RootDocument,
	notFoundComponent: () => <NotFoundView />,
});

function ThemeShortcut() {
	useThemeShortcut();
	return null;
}

function CurrencyInitializer() {
	const setCurrency = useSetAtom(currencyAtom);
	useEffect(() => {
		const stored = localStorage.getItem("currency-preference");
		if (!stored) {
			setCurrency(detectDefaultCurrency());
		}
	}, [setCurrency]);
	return null;
}

const WEB_MCP_SCRIPT = `
(() => {
	const modelContext = navigator.modelContext;
	if (!modelContext?.registerTool) return;
	const controller = new AbortController();
	const json = (value) => ({ content: [{ type: "json", json: value }] });
	modelContext.registerTool({
		name: "agentmrr.searchAgents",
		description: "Search public AgentMRR listings by name, description, or category.",
		inputSchema: {
			type: "object",
			properties: {
				query: { type: "string" },
				limit: { type: "number", minimum: 1, maximum: 20 }
			},
			required: ["query"]
		},
		async execute(input) {
			const params = new URLSearchParams({
				query: String(input.query || ""),
				limit: String(input.limit || 5)
			});
			const response = await fetch("/api/agents/search?" + params.toString(), {
				headers: { Accept: "application/json" }
			});
			return json(await response.json());
		}
	}, { signal: controller.signal });
	modelContext.registerTool({
		name: "agentmrr.readLeaderboard",
		description: "Read the public AgentMRR revenue leaderboard.",
		inputSchema: {
			type: "object",
			properties: {
				limit: { type: "number", minimum: 1, maximum: 20 }
			}
		},
		async execute(input) {
			const params = new URLSearchParams({ limit: String(input.limit || 10) });
			const response = await fetch("/api/agents/leaderboard?" + params.toString(), {
				headers: { Accept: "application/json" }
			});
			return json(await response.json());
		}
	}, { signal: controller.signal });
	window.addEventListener("pagehide", () => controller.abort(), { once: true });
})();
`;

function RootDocument() {
	return (
		<html lang="en" suppressHydrationWarning>
			<head suppressHydrationWarning>
				<HeadContent />
				<JsonLd data={[organizationSchema(), websiteSchema()]} />
			</head>
			<body>
				<script
					async
					src="https://www.googletagmanager.com/gtag/js?id=G-JHJJY2GQ38"
				/>
				<script
					// biome-ignore lint/security/noDangerouslySetInnerHtml: GTM initialization
					dangerouslySetInnerHTML={{
						__html: `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-JHJJY2GQ38');`,
					}}
				/>
				<script
					// biome-ignore lint/security/noDangerouslySetInnerHtml: registers WebMCP tools when the browser exposes the API
					dangerouslySetInnerHTML={{ __html: WEB_MCP_SCRIPT }}
				/>
				<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
					<ThemeShortcut />
					<CurrencyInitializer />
					<LazyMotionProvider>
						<AuthQueryProvider>
							<PostHogAnalyticsProvider>
								<SubscriptionProvider>
									<AdvertiseDialogProvider>
										<ListAgentDialogProvider>
											<AddAgentDialogProvider>
												<SellStartupDialogProvider>
													<ClaimWebsiteDialogProvider>
														<Outlet />
													</ClaimWebsiteDialogProvider>
												</SellStartupDialogProvider>
											</AddAgentDialogProvider>
										</ListAgentDialogProvider>
									</AdvertiseDialogProvider>
									<Suspense fallback={null}>
										<AppToaster />
									</Suspense>
								</SubscriptionProvider>
							</PostHogAnalyticsProvider>
						</AuthQueryProvider>
					</LazyMotionProvider>
				</ThemeProvider>
				<Scripts />
			</body>
		</html>
	);
}
